import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { StaticQuery, graphql } from 'gatsby';

import { IntlContext } from '../../Providers/Intl';
import Blade from '../Blade';
import { Context } from '../../Providers/Blade';
import SkipToContent from '../SkipToContent';
import PromoLink from '../PromoLink';
import MenuItem from '../MenuItem';

import { MobileNav, DesktopNav } from './layout';
import {
  StyledHeader,
  Wrapper,
  LogoLink,
  Logo,
  Menu,
  Play,
  Overlay,
} from './styles';

export const Header = ({ data }) => {
  if (!data) return null;

  // * We keep the state for the header menu contained within contentxt
  const { state, resetState } = useContext(Context);

  // * Destructuring the fields off of data
  const {
    image: logo,
    imageSharp: logoSharp,
    menu,
    loginLink,
    loginText,
    playLink,
    playText,
  } = data;

  const [openSections, setOpenSections] = useState({});

  // * We use the double ! because if the label doesnt exist in state it will return undefined, and by using the double ! we can convert the undefined to false
  const onClick = label => {
    const isOpen = !!openSections[label];
    setOpenSections({
      [label]: !isOpen,
    });
  };

  return (
    <>
      <Overlay
        isOpen={Object.values(openSections).includes(true)}
        onClick={() => setOpenSections({})}
      />
      <StyledHeader>
        <SkipToContent />
        <Wrapper className="auto-margin">
          <LogoLink className="inline" to="/">
            <Logo src={logoSharp.publicURL} alt={logo.altTag} />
          </LogoLink>
          <DesktopNav
            menu={menu}
            openSections={openSections}
            onClick={onClick}
            loginLink={loginLink}
            loginText={loginText}
            playLink={playLink}
            playText={playText}
            resetState={() => setOpenSections({})}
          />
          <MobileNav loginLink={loginLink} loginText={loginText} />
        </Wrapper>
        <Blade
          title="test-blade"
          isOpen={state}
          logo={logo}
          logoSharp={logoSharp}
        >
          <>
            {menu && (
              <Menu className="no-bullets">
                {menu.map((info, index) => {
                  return (
                    <li key={`menu-item-${index.toString()}`}>
                      <MenuItem
                        data={info}
                        isOpen={!!openSections[info.text]}
                        label={info.text}
                        onClick={onClick}
                        resetState={() => resetState()}
                      />
                    </li>
                  );
                })}
              </Menu>
            )}
            <PromoLink
              to={loginLink.url}
              className="ghost-on-light-bg"
              ariaLabel={loginLink.ariaLabelLogin}
              queryString={loginLink.queryStringLogin}
            >
              {loginText}
            </PromoLink>
            <Play
              to={playLink.url}
              className="solid-on-light-bg"
              ariaLabel={playLink.ariaLabelPlay}
              queryString={playLink.queryStringPlay}
            >
              {playText}
            </Play>
          </>
        </Blade>
      </StyledHeader>
    </>
  );
};

Header.propTypes = {
  data: PropTypes.shape().isRequired,
};

Header.defaultProps = {};

const withStaticQueryHeader = () => {
  return (
    <StaticQuery
      query={graphql`
        query headerQuery {
          prodigy {
            headerFooter {
              header {
                lang
                image {
                  url
                  altTag
                }
                imageSharp {
                  publicURL
                }
                loginText
                loginLink {
                  url
                }
                playText
                playLink {
                  url
                }
                menu {
                  text
                  type
                  url
                  subitems {
                    text
                    type
                    url
                    subitems {
                      text
                      type
                      url
                      linkType
                      queryString
                      ariaLabel
                    }
                    queryString
                    ariaLabel
                  }
                  ariaLabel
                  queryString
                }
                queryStringPlay
                queryStringLogin
                ariaLabelPlay
                ariaLabelLogin
              }
            }
          }
        }
      `}
      render={({ prodigy }) => {
        const { intl } = useContext(IntlContext);
        const { header } = prodigy.headerFooter.find(
          ({ header: headerData }) => headerData.lang === intl.lang
        );
        return <Header data={header} />;
      }}
    />
  );
};

export default withStaticQueryHeader;
